<template>
  <div
    v-if="state.status !== 'loaded' || policy || canEdit"
    class="mt-4 flex min-h-8 items-center gap-3 text-sm"
    data-testid="runner-autoscaling"
  >
    <ArrowsPointingOutIcon
      class="size-5 shrink-0 text-gray-400 dark:text-gray-500"
      aria-hidden="true"
    />

    <p v-if="state.status === 'loading'" class="text-gray-400 dark:text-gray-500">
      Loading autoscaling…
    </p>

    <template v-else-if="state.status === 'error'">
      <p class="flex-1 text-gray-500 dark:text-gray-400">Autoscaling could not be loaded.</p>
      <button
        type="button"
        class="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
        @click="emit('retry')"
      >
        Retry
      </button>
    </template>

    <template v-else-if="policy">
      <div class="min-w-0 flex-1">
        <RouterLink
          :to="pageRoute"
          class="block font-medium break-words hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          :class="
            policy.enabled ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
          "
          data-testid="runner-autoscaling-summary"
        >
          {{ scalingSummary(policy) }}
        </RouterLink>
        <p
          class="text-xs break-words"
          :class="
            policy.status.disabledReason || cleanupBlocked(policy) || jobLimitMissing(policy)
              ? 'text-amber-700 dark:text-amber-400'
              : 'text-gray-500 dark:text-gray-400'
          "
          data-testid="runner-autoscaling-hint"
        >
          {{
            jobLimitMissing(policy) && !policy.status.disabledReason && !cleanupBlocked(policy)
              ? JOB_LIMIT_HINT
              : scalingHint(policy)
          }}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        v-if="canEdit"
        :aria-checked="policy.enabled"
        :aria-label="policy.enabled ? 'Disable autoscaling' : 'Enable autoscaling'"
        :title="policy.enabled ? 'Disable autoscaling' : 'Enable autoscaling'"
        :disabled="toggling"
        class="relative inline-flex h-5 w-9 shrink-0 rounded-full p-0.5 outline-offset-2 outline-indigo-600 transition-colors duration-200 ease-in-out focus-visible:outline-2 disabled:cursor-wait disabled:opacity-50"
        :class="
          policy.enabled ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-200 dark:bg-white/10'
        "
        data-testid="runner-autoscaling-toggle"
        @click="toggle"
      >
        <span
          aria-hidden="true"
          class="size-4 rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out"
          :class="policy.enabled ? 'translate-x-4' : 'translate-x-0'"
        />
      </button>
      <RouterLink
        :to="pageRoute"
        class="inline-flex shrink-0 items-center rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-200"
        :aria-label="canEdit ? 'Edit autoscaling' : 'Show autoscaling'"
        :title="canEdit ? 'Edit autoscaling' : 'Show autoscaling'"
        data-testid="runner-autoscaling-edit"
      >
        <PencilSquareIcon class="size-5" aria-hidden="true" />
      </RouterLink>
    </template>

    <RouterLink
      v-else
      :to="pageRoute"
      class="text-left font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
      data-testid="runner-autoscaling-add"
    >
      Add autoscaling
      <span class="font-normal text-gray-500 dark:text-gray-400">· burst runners when busy</span>
    </RouterLink>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { ArrowsPointingOutIcon, PencilSquareIcon } from '@heroicons/vue/20/solid';
import { type ScalingAnchor, type ScalingPolicy, setScalingPolicyEnabled } from '@/api/scaling';
import { runnerProblem } from '@/api/runners';
import type { ScalingState } from '@/lib/runner-scaling';
import { cleanupBlocked, jobLimitMissing, scalingHint, scalingSummary } from '@/lib/scaling';
import { useToastStore } from '@/stores/toast';

const JOB_LIMIT_HINT = 'No job limit reported yet, so free job slots cannot be counted.';

const props = defineProps<{
  anchor: ScalingAnchor;
  state: ScalingState;
  canEdit: boolean;
}>();

const emit = defineEmits<{
  (e: 'change', policy: ScalingPolicy | null): void;
  (e: 'retry'): void;
}>();

const toast = useToastStore();
const toggling = ref(false);

const policy = computed(() => (props.state.status === 'loaded' ? props.state.policy : null));
const pageRoute = computed(() => ({
  name: props.anchor.kind === 'EXTERNAL' ? 'ExternalRunnerAutoscaling' : 'RunnerAutoscaling',
  params: { id: props.anchor.runnerId },
}));

async function toggle() {
  if (!policy.value || toggling.value) return;
  toggling.value = true;
  const enable = !policy.value.enabled;
  try {
    emit('change', await setScalingPolicyEnabled(props.anchor, enable));
  } catch (e) {
    toast.error(
      runnerProblem(e, `Autoscaling could not be ${enable ? 'enabled' : 'disabled'}.`).message
    );
  } finally {
    toggling.value = false;
  }
}
</script>
